import React, { useState } from "react";
import styled from "styled-components";
import { RebelColors, RebelModal, RebelModalHeader, RebelModalBody, RebelModalFooter, RebelAccentButton, RebelSecondaryButton, RebelLabel, RebelInput } from "../../../styles/rebel-experiences";
import { generateGUID } from "../../../utils/database/firebase-budget-tool";
import { setDocument, updateDocumentProperty } from "../../../utils/firestore-functions";
import emailjs from "@emailjs/browser";
import firebase from "firebase/compat/app";

const EMAILJS_SERVICE_ID = "service_xiqdcy8";
const EMAILJS_TEMPLATE_ID = "template_ozs41kd";
const EMAILJS_PUBLIC_KEY = "user_8Qt3ZJv9dlnGhiJefkhBh";

const RebelBudgetSaveModal = ({ isOpen, toggle, config, properties, rankings, budgetId, setBudgetId, budgetDB, setSavedEmail }) => {
    const [email, setEmail] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [savedSuccess, setSavedSuccess] = useState(false);
    const [savedLink, setSavedLink] = useState("");

    const handleSave = async () => {
        if (!email || !email.includes("@")) {
            alert("Please enter a valid email address.");
            return;
        }

        setIsSaving(true);

        try {
            const docId = budgetId || generateGUID();
            const now = firebase.firestore.Timestamp.now();

            const data = {
                id: docId,
                email: email,
                companyName: config.companyName,
                startDate: config.startDate,
                endDate: config.endDate,
                attendees: config.attendees,
                currency: config.currency,
                properties: properties,
                rankings: rankings || {},
                updatedAt: now,
            };

            if (!budgetId) {
                data.createdAt = now;
            }

            if (budgetId) {
                // Update existing document
                Object.entries(data).forEach(([key, value]) => {
                    updateDocumentProperty(budgetDB, "rebelClientBudgets", docId, key, value);
                });
            } else {
                // Create new document
                await setDocument(budgetDB, "rebelClientBudgets", docId, data);
            }

            // Update URL without reload
            const baseUrl = window.location.origin;
            const resumeLink = `${baseUrl}/budget-app/rebel-budget/${docId}`;
            window.history.replaceState(null, "", `/budget-app/rebel-budget/${docId}`);
            setBudgetId(docId);
            setSavedLink(resumeLink);

            // Send email
            try {
                const generatedDate = new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

                await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
                    to_email: email,
                    company_name: config.companyName || "Your Team",
                    start_date: config.startDate || "TBD",
                    end_date: config.endDate || "TBD",
                    attendees: config.attendees,
                    num_properties: properties.length,
                    resume_link: resumeLink,
                    generated_date: generatedDate,
                }, EMAILJS_PUBLIC_KEY);
            } catch (emailError) {
                console.warn("Email sending failed (template may not be configured yet):", emailError);
            }

            if (setSavedEmail) setSavedEmail(email);
            setSavedSuccess(true);
            setIsSaving(false);
        } catch (error) {
            console.error("Error saving budget:", error);
            alert("Failed to save. Please try again.");
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setSavedSuccess(false);
        setSavedLink("");
        toggle();
    };

    const copyLink = () => {
        navigator.clipboard.writeText(savedLink);
    };

    if (savedSuccess) {
        return (
            <RebelModal isOpen={isOpen} toggle={handleClose}>
                <RebelModalHeader>Saved Successfully</RebelModalHeader>
                <RebelModalBody>
                    <SuccessMessage>Your budget comparison has been saved!</SuccessMessage>
                    <LinkContainer>
                        <RebelLabel>Your resume link:</RebelLabel>
                        <LinkDisplay>{savedLink}</LinkDisplay>
                        <CopyButton onClick={copyLink}>Copy Link</CopyButton>
                    </LinkContainer>
                    <EmailNote>
                        A copy has been sent to <strong>{email}</strong>
                    </EmailNote>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={handleClose}>Done</RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>
        );
    }

    return (
        <RebelModal isOpen={isOpen} toggle={handleClose}>
            <RebelModalHeader>Save & Share</RebelModalHeader>
            <RebelModalBody>
                <p style={{ color: RebelColors.textMuted, marginBottom: '1rem' }}>
                    Save your property comparison and receive a link to resume later.
                </p>
                <RebelLabel>Email Address</RebelLabel>
                <RebelInput
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your.email@company.com"
                />
            </RebelModalBody>
            <RebelModalFooter>
                <RebelSecondaryButton onClick={handleClose}>Cancel</RebelSecondaryButton>
                <RebelAccentButton onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "Saving..." : budgetId ? "Update & Send" : "Save & Send"}
                </RebelAccentButton>
            </RebelModalFooter>
        </RebelModal>
    );
};

export default RebelBudgetSaveModal;

const SuccessMessage = styled.p`
    color: ${RebelColors.success};
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 1.5rem;
`;

const LinkContainer = styled.div`
    margin-bottom: 1rem;
`;

const LinkDisplay = styled.div`
    background-color: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.gold};
    border-radius: 6px;
    padding: 10px 12px;
    color: ${RebelColors.gold};
    font-size: 0.85rem;
    word-break: break-all;
    margin-bottom: 0.5rem;
`;

const CopyButton = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${RebelColors.gold};
        color: #000;
    }
`;

const EmailNote = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
    text-align: center;

    strong {
        color: ${RebelColors.text};
    }
`;
